import React, { useState, useEffect, useCallback } from 'react';
import { fetchTeamData, fetchSearchResults, fetchPersonData, fetchTeamMemberships } from '../../../utils/api';
import { calculateConfidenceScore } from '../../../utils/utils';
import TeamInformation from './TeamInformation';
import NavigationButtons from './NavigationButtons';
import ProgressBar from './ProgressBar';
import SearchResultItem from './SearchResultItem'; 
import PersonInformation from "./PersonInformation";
import TeamMemberships from "./TeamMemberships";
function ReconciliationStep({ ids, savedData, onSaveData, onFinish, type, autoAccept, currentIndex, setCurrentIndex, sessionKey, setSessionKey }) {
  const [teamResult, setTeamResult] = useState(null);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedHit, setSelectedHit] = useState(null);
  const [iframeUrl , setIframeUrl] = useState(null)
  const [personResult, setPersonResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // Generate a unique session key if not already set
    if (!sessionKey) {
      const newSessionKey = `reconciliation_${type}_${Date.now()}`;
      setSessionKey(newSessionKey);
    }

    // Load saved session data from local storage
    const savedSession = localStorage.getItem(sessionKey);
    if (savedSession) {
      const parsedSession = JSON.parse(savedSession);
      setCurrentIndex(parsedSession.currentIndex);
      onSaveData(parsedSession.savedData);
    }
  }, [sessionKey, type, onSaveData]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    // Save session data to local storage whenever it changes
    if (sessionKey) {
      const sessionData = {
        currentIndex,
        savedData,
        type,
        ids,
      };
      localStorage.setItem(sessionKey, JSON.stringify(sessionData));
    }
  }, [currentIndex, savedData, sessionKey, type, ids]);

  const fetchData = useCallback(async () => {
    try {
      if(type === "team"){
      const id = ids[currentIndex];
      //console.log(`Fetching data for ID: ${id}`);
      const teamData = await fetchTeamData(id);
      //console.log("Fetched team data:", teamData);
      setTeamResult(teamData[0]); // Assuming the API returns an array with one team object

      if (teamData[0]) {
        const searchData = await fetchSearchResults(teamData[0].name);
        // Filter hits that don't have classification_id equals to 'team'
        const filteredHits = searchData.results[0].hits.filter(hit => hit.document.classification_id === 'team');
        
        // Calculate confidence scores for filtered hits
        const hitsWithScores = filteredHits.map(hit => ({
          ...hit,
          confidenceScore: calculateConfidenceScore(teamData[0], hit.document, 'team')
        }));

        // Sort hits by confidence score in descending order
        hitsWithScores.sort((a, b) => b.confidenceScore - a.confidenceScore);

        searchData.results[0].hits = hitsWithScores;
        setSearchResult(searchData);
        console.log(autoAccept);
        // Auto accept results with confidence score > 80%
        
      } else {
        console.log("No team data available, skipping search");
      }
    } else if (type === "person"){
      const id = ids[currentIndex];
      console.log(`Fetching data for ID: ${id}`);
      const personData = await fetchPersonData(id);
      console.log("Fetched person data:", personData);
      setPersonResult(personData);

      if (personData) {
        const memberships = await fetchTeamMemberships(id);

        setTeamResult(memberships);

        const searchData = await fetchSearchResults(personData.usualName);
        const filteredHits = searchData.results[0].hits.filter(hit => hit.document.classification_id === 'player');
        
        // Calculate confidence scores for filtered hits
        const hitsWithScores = filteredHits.map(hit => ({
          ...hit,
          confidenceScore: calculateConfidenceScore(personData, hit.document, 'person', memberships)
        }));

        // Sort hits by confidence score in descending order
        hitsWithScores.sort((a, b) => b.confidenceScore.score - a.confidenceScore.score);

        searchData.results[0].hits = hitsWithScores;
        console.log("Fetched search data:", searchData);
        setSearchResult(searchData);
      } else {
        console.log("No person data available, skipping search");
      }
    }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [currentIndex, ids, type]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prevIndex => prevIndex - 1);
      setSelectedHit(null);
      setIframeUrl(null);
    }
  }, [currentIndex]);

  const onNext = useCallback(() => {
    if (currentIndex < ids.length - 1) {
      setCurrentIndex(prevIndex => prevIndex + 1);
      setSelectedHit(null);
      setIframeUrl(null);
    }
  }, [currentIndex, ids.length]);

  const onFinishReconciliation = () => {
    // Clear the session data from local storage when finished
    if (sessionKey) {
      localStorage.removeItem(sessionKey);
    }
    onFinish();
  };

  const onHitClick = (index) => {
    setSelectedHit(index);
    setIframeUrl(computeIframeUrl(searchResult.results[0].hits[index]));
  };

  const computeIframeUrl = (hit) => {
    if (type === "team") {
      return `https://sortitoutsi.net/football-manager-2024/team/${hit.document.fm_id}/${hit.document.name.replace(" ", "-")}`;
    } else if (type === "person") {
      return `https://sortitoutsi.net/football-manager/person/${hit.document.fm_id}/${hit.document.name.replace(" ", "-")}`;
    }
  };

  const onFootballManagerIdChange = (e) => {
    updateFootballManagerId(e.target.value);
  };

  const updateFootballManagerId = (fmId) => {
    const updatedData = [...savedData];
    const currentData = updatedData.find(data => data.id === ids[currentIndex]) || { id: ids[currentIndex] };
    currentData.footballManagerId = fmId;
    const index = updatedData.findIndex(data => data.id === ids[currentIndex]);
    if (index !== -1) {
      updatedData[index] = currentData;
    } else {
      updatedData.push(currentData);
    }
    onSaveData(updatedData);
  };

  if (!teamResult) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <ProgressBar current={currentIndex + 1} total={ids.length} />
      <NavigationButtons
        onPrev={onPrev}
        onNext={onNext}
        onFinish={onFinishReconciliation}
        isFirst={currentIndex === 0}
        isLast={currentIndex === ids.length - 1}
      />
      <div className="mb-4">
        {type === "team" && (
        <TeamInformation
          teamResult={teamResult}
          savedData={savedData}
          currentId={ids[currentIndex]}
          onFootballManagerIdChange={onFootballManagerIdChange}
        />
        )
        }
        {type === "person" && ( 
             <PersonInformation
              personResult={personResult}
              savedData={savedData}
              currentId={ids[currentIndex]}
              onFootballManagerIdChange={onFootballManagerIdChange}
              />
        )
        }
        {searchResult && searchResult.results && searchResult.results[0] && (
          <>
            <h2 className="text-xl font-bold mb-2">Search Results</h2>
            <ul className="space-y-2 max-h-96 overflow-auto border-solid border-2 border-gray-300 rounded p-2">
            {searchResult.results[0].hits.map((hit, index) => (
                <SearchResultItem
                  key={index}
                  hit={hit}
                  index={index}
                  isSelectedHit={selectedHit === index}
                  onHitClick={onHitClick}
                  updateFootballManagerId={updateFootballManagerId}
                  teamResult={teamResult}
                  personResult={personResult}
                  type={type}
                />
              ))}
            </ul>
            
            {selectedHit !== null && (
              <div className="grid grid-cols-3">
              {type === "person" && (
                <TeamMemberships
                teamResult={teamResult}
                />
              )}
              <div className="mt-4 col-span-2">
                <iframe
                  src={iframeUrl}
                  title='Football Manager'
                  width="100%"
                  height="800"
                  className="border-2 border-gray-300 rounded"
                />
              </div>
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
}

export default ReconciliationStep;
